import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Package, PencilSimple, Plus, Trash } from '@phosphor-icons/react'
import { DataTable, EmptyState, Field, Input, Select, type Column } from '@/ui'
import { useCan } from '@/auth/context'
import { errorMessage } from '../../api/core'
import { useDeleteSku, useSaveSku, useSkus, type SkuRow } from '../../api/packFull'
import { useCrops } from '../../api/reference'
import { ActionButton } from '../../components/Can'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { FilterBar, SearchFilter, SelectFilter } from '../../components/FilterBar'
import { FormDialog } from '../../components/FormDialog'
import { ConsolePage } from '../../components/Layout'
import { Code } from '../../components/bits'
import { RefusalBanner } from '../../components/pack/Refusal'
import { useUrlState } from '../../lib/useUrlState'
import { cleanGtin } from '../../model/pack/gtin'
import {
  createSkuBody,
  draftFromSku,
  emptySkuDraft,
  minPouchG,
  normaliseSkuCode,
  skuOk,
  updateSkuBody,
  validateSkuDraft,
  type SkuDraft,
} from '../../model/pack/skuForm'

const LIMIT = 50

type Editing = { mode: 'create' } | { mode: 'edit'; row: SkuRow }

/** Products (F7.1-a): what is packed — pack size, shelf life, GTIN, carton size. */
export function SkusPage() {
  const [f, setF, { reset, active }] = useUrlState({ crop: '', q: '', page: 1 })
  const q = useSkus({
    cropId: f.crop || undefined,
    search: f.q || undefined,
    page: f.page,
    limit: LIMIT,
  })
  const crops = useCrops()
  const can = useCan()
  const [editing, setEditing] = useState<Editing | null>(null)
  const [removing, setRemoving] = useState<SkuRow | null>(null)

  const columns: Column<SkuRow>[] = [
    { id: 'code', header: 'Code', cell: (r) => <Code>{r.code}</Code> },
    { id: 'name', header: 'Product', cell: (r) => r.name },
    { id: 'crop', header: 'Crop', cell: (r) => r.cropName ?? '—', hideBelow: 'md' },
    {
      id: 'net',
      header: 'Pack size',
      align: 'right',
      mono: true,
      cell: (r) => `${r.netG.toLocaleString('en-IN')} g`,
    },
    {
      id: 'tol',
      header: 'Lowest pouch',
      align: 'right',
      mono: true,
      hideBelow: 'lg',
      cell: (r) => {
        const min = minPouchG({ netG: String(r.netG), toleranceG: r.toleranceG ?? '' })
        return min === null ? '—' : `${min.toLocaleString('en-IN')} g`
      },
    },
    {
      id: 'shelf',
      header: 'Shelf life',
      align: 'right',
      cell: (r) =>
        r.shelfLifeDays ? `${r.shelfLifeDays} days` : <span className="text-warn">Not set</span>,
    },
    {
      id: 'carton',
      header: 'Per carton',
      align: 'right',
      mono: true,
      hideBelow: 'md',
      cell: (r) => r.pouchesPerCarton ?? '—',
    },
    {
      id: 'gtin',
      header: 'GTIN',
      hideBelow: 'lg',
      cell: (r) => (r.gtin ? <Code>{r.gtin}</Code> : <span className="text-muted">None yet</span>),
    },
    {
      id: 'actions',
      header: <span className="sr-only">Actions</span>,
      align: 'right',
      cell: (r) => (
        <span className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          <ActionButton
            perm="skus:update"
            hide
            size="sm"
            variant="ghost"
            leadingIcon={<PencilSimple />}
            onClick={() => setEditing({ mode: 'edit', row: r })}
          >
            Edit
          </ActionButton>
          <ActionButton
            perm="skus:delete"
            hide
            size="sm"
            variant="ghost"
            leadingIcon={<Trash />}
            onClick={() => setRemoving(r)}
          >
            Delete
          </ActionButton>
        </span>
      ),
    },
  ]

  return (
    <ConsolePage
      eyebrow="Pack & lab"
      title="SKUs"
      description="Each product we pack: pouch size, the shortfall allowed per pouch, shelf life (it sets the best-before date) and how many pouches go in a carton. A product needs an approved label at a hall before it can be packed there."
      actions={
        <ActionButton
          perm="skus:create"
          leadingIcon={<Plus />}
          onClick={() => setEditing({ mode: 'create' })}
        >
          Add product
        </ActionButton>
      }
    >
      <DataTable<SkuRow>
        caption="SKUs"
        columns={columns}
        rows={q.data?.rows ?? []}
        getRowId={(r) => r.id}
        loading={q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        onRowClick={can('skus:update') ? (r) => setEditing({ mode: 'edit', row: r }) : undefined}
        pageSize={LIMIT}
        server={{ page: f.page, total: q.data?.total ?? 0, onPageChange: (page) => setF({ page }) }}
        empty={
          <EmptyState
            icon={<Package />}
            title={active ? 'No products match' : 'No products yet'}
            description={
              active
                ? 'Try another crop or search.'
                : 'Add the first product to start planning pack runs.'
            }
            compact
          />
        }
        filters={
          <FilterBar onClear={reset} active={active}>
            <SelectFilter
              label="Crop"
              value={f.crop}
              onChange={(crop) => setF({ crop })}
              options={(crops.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
            />
            <SearchFilter
              label="Search"
              value={f.q}
              onChange={(v) => setF({ q: v })}
              placeholder="Code or name"
            />
          </FilterBar>
        }
      />
      <p className="text-[13px] text-muted">
        Labels for each product are kept under{' '}
        <Link to="/c/labels" className="underline underline-offset-2">
          Labels
        </Link>
        .
      </p>
      {editing && <SkuDialog editing={editing} onClose={() => setEditing(null)} />}
      <DeleteSku row={removing} onClose={() => setRemoving(null)} />
    </ConsolePage>
  )
}

function SkuDialog({ editing, onClose }: { editing: Editing; onClose: () => void }) {
  const edit = editing.mode === 'edit' ? editing.row : null
  const [d, setD] = useState<SkuDraft>(edit ? draftFromSku(edit) : emptySkuDraft())
  const [touched, setTouched] = useState<Set<keyof SkuDraft>>(new Set())
  const [tried, setTried] = useState(false)
  const save = useSaveSku({ inline: true })
  const crops = useCrops()
  const p = validateSkuDraft(d, editing.mode)
  const touch = (k: keyof SkuDraft) => setTouched((s) => new Set(s).add(k))
  const shown = (k: keyof SkuDraft) => (tried || touched.has(k) ? p[k] : undefined)
  const set = (patch: Partial<SkuDraft>) => setD((x) => ({ ...x, ...patch }))
  const min = minPouchG(d)
  const gtinDigits = cleanGtin(d.gtin)

  return (
    <FormDialog
      open
      onClose={onClose}
      title={edit ? `Edit ${edit.code}` : 'Add product'}
      description={
        edit
          ? 'Code, crop and pack size stay as they are; make a new product to change them.'
          : undefined
      }
      saveLabel={edit ? 'Save' : 'Add product'}
      loading={save.isPending}
      onSave={async () => {
        setTried(true)
        if (!skuOk(p)) throw new Error('invalid')
        await save.mutateAsync(
          edit ? { id: edit.id, body: updateSkuBody(d) } : { body: createSkuBody(d) },
        )
      }}
    >
      {save.isError && <RefusalBanner error={save.error} />}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field
          label="Code"
          required={!edit}
          error={shown('code')}
          hint={edit ? undefined : 'e.g. GN-K-500'}
        >
          <Input
            value={d.code}
            disabled={Boolean(edit)}
            onChange={(e) => set({ code: normaliseSkuCode(e.target.value) })}
            onBlur={() => touch('code')}
            className="font-mono"
            maxLength={20}
            data-autofocus={edit ? undefined : true}
          />
        </Field>
        <Field label="Crop" required={!edit} error={shown('cropId')}>
          {edit ? (
            <Input value={edit.cropName ?? ''} disabled />
          ) : (
            <Select
              value={d.cropId}
              onChange={(cropId) => set({ cropId })}
              onBlur={() => touch('cropId')}
              placeholder="Pick the crop"
              options={(crops.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
            />
          )}
        </Field>
      </div>
      <Field
        label="Product name"
        required
        error={shown('name')}
        hint="As printed on the pouch, e.g. Groundnut kernel 500 g"
      >
        <Input
          value={d.name}
          onChange={(e) => set({ name: e.target.value })}
          onBlur={() => touch('name')}
          maxLength={120}
          data-autofocus={edit ? true : undefined}
        />
      </Field>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Net weight per pouch" required={!edit} error={shown('netG')}>
          <Input
            inputMode="numeric"
            value={d.netG}
            disabled={Boolean(edit)}
            onChange={(e) => set({ netG: e.target.value.replace(/\D/g, '') })}
            onBlur={() => touch('netG')}
            trailing="g"
            className="font-mono"
            placeholder="500"
          />
        </Field>
        <Field
          label="Shortfall allowed per pouch"
          error={shown('toleranceG')}
          hint={
            min !== null && !p.toleranceG
              ? `Pouches under ${min.toLocaleString('en-IN')} g are flagged`
              : 'Grams, e.g. 15'
          }
        >
          <Input
            inputMode="decimal"
            value={d.toleranceG}
            onChange={(e) => set({ toleranceG: e.target.value.replace(/[^\d.]/g, '') })}
            onBlur={() => touch('toleranceG')}
            trailing="g"
            className="font-mono"
            placeholder="15"
          />
        </Field>
        <Field
          label="Shelf life"
          required
          error={shown('shelfLifeDays')}
          hint="Best-before = pack date + these days"
        >
          <Input
            inputMode="numeric"
            value={d.shelfLifeDays}
            onChange={(e) => set({ shelfLifeDays: e.target.value.replace(/\D/g, '') })}
            onBlur={() => touch('shelfLifeDays')}
            trailing="days"
            className="font-mono"
            placeholder="180"
          />
        </Field>
        <Field
          label="Pouches per carton"
          error={shown('pouchesPerCarton')}
          hint="Used for the carton labels"
        >
          <Input
            inputMode="numeric"
            value={d.pouchesPerCarton}
            onChange={(e) => set({ pouchesPerCarton: e.target.value.replace(/\D/g, '') })}
            onBlur={() => touch('pouchesPerCarton')}
            className="font-mono"
            placeholder="24"
          />
        </Field>
      </div>
      <Field
        label="GTIN (barcode number)"
        error={shown('gtin')}
        hint={
          gtinDigits && !p.gtin
            ? `Check digit is right · stored as ${gtinDigits.padStart(14, '0')}`
            : 'Optional until we have GS1 numbers: 8, 12, 13 or 14 digits.'
        }
      >
        <Input
          inputMode="numeric"
          value={d.gtin}
          onChange={(e) => set({ gtin: e.target.value.replace(/[^\d\s-]/g, '') })}
          onBlur={() => touch('gtin')}
          className="font-mono"
          maxLength={20}
        />
      </Field>
    </FormDialog>
  )
}

function DeleteSku({ row, onClose }: { row: SkuRow | null; onClose: () => void }) {
  const del = useDeleteSku()
  const close = () => {
    del.reset()
    onClose()
  }
  return (
    <ConfirmDialog
      open={Boolean(row)}
      onClose={close}
      title={`Delete ${row?.code ?? ''}?`}
      description="Only a product that was never packed can be deleted."
      confirmLabel="Delete product"
      tone="danger"
      loading={del.isPending}
      onConfirm={async () => {
        if (!row) return
        await del.mutateAsync(row.id)
        close()
      }}
    >
      {del.isError && <RefusalBanner error={del.error} />}
    </ConfirmDialog>
  )
}
